import API from '../../services/api.svelte.js';
import { get as getStore } from "svelte/store";
import { locale, format } from "svelte-i18n";
import { CVSS } from "@turingpointde/cvss.js";

const KB_URL = "https://gitlab.com/securetia/vulseek/kb/-/tree/master/vulnerability";

function setLocale(){
    let setLocale = getStore(locale);
    if(setLocale != "es" && setLocale != "en") setLocale = 'es';
    locale.set(setLocale);
}


export const fetchVulnerabilities = (resource = "v2/vulnerability/", config = {}) => {
    return API.search(resource, config);
};

export const findVulnerability = (id, config = {}) => {
    return API.find('v2/vulnerability', id, config)
};

export const saveVulnerability = (data, config = {}, is_upsert = false) => {
    return API.save("v2/vulnerability/custom", data, [], config, is_upsert);
};

export const fetchVulnerabilitiesProperties = async (config = { filter: {} }) => {
    return API.search('v2/vulnerability-property/', config);
}

export const fetchVulnerabilitiesPropertiesCount = async (config = { filter: {} }) => {
    return API.search("v2/vulnerability-property/count/", config);
}

export const fetchVulnerabilitiesPropertiesCountByKind = async (config = { filter: {} }) => {
    return API.search("v2/vulnerability-property/count_by_kind/", config);
}

export function getNote(id, config = {}) {
    return API.search('v2/vulnerability/' + id + '/note', config);
}

export function upsertNote(vul_id, data, config = {}) {
    return API.upsert('v2/vulnerability/' + vul_id + '/note', data, [], config);
}

export function deleteNote(vul_id, config = {}) {
    return API.del('v2/vulnerability/' + vul_id + '/note', "", config);
}

export function fetchCWE(config = {}) {
    return API.search('cwe', config);
}

export function findCWE(id, config = {}) {
    return API.find('cwe', id, config);
}

export function buildCVEtitles(vulnerabilities = []) {
    for (let vulnerability of vulnerabilities) {
        if (vulnerability.id.includes("CVE-")) {
            if (!vulnerability.title) {
                vulnerability.title = vulnerability.id;
            }
            else if (!vulnerability.title_es) {
                vulnerability.title_es = vulnerability.title
            }
            else {
                vulnerability.title = `${vulnerability.title} (${vulnerability.id})`
                vulnerability.title_es = `${vulnerability.title_es} (${vulnerability.id})`
            }
        }
    }
    return vulnerabilities;
}


export function attackVectorOrder(attack_vector) {
    return {
        network: 0,
        adjacent: 1,
        local: 2,
        physical: 3,
    }[attack_vector];
}

export function buildCVSSvector(data = {}) {
    let vector = {
        CVSS: "3.1",
        AV: data.AV,
        AC: data.AC,
        PR: data.PR,
        UI: data.UI,
        S: data.S,
        C: data.C,
        I: data.I,
        A: data.A,
    };
    return CVSS(vector).vector;
}

export function parseCVSSvector(vulnerability) {

    let cvss_data_spanish = {};
    let metrics;

    if (vulnerability.cvss3_vector) {
        const vectorV3 = CVSS(vulnerability.cvss3_vector);
        metrics = vectorV3.getDetailedVectorObject().metrics;
        Object.entries(metrics).forEach(([name, obj]) => cvss_data_spanish[name] = getCVSS_v3_vectorValues(name, metrics[name].value));
    }
    else {
        if (vulnerability.cvss2_vector) {
            const vectorV2 = vulnerability.cvss2_vector;
            metrics = parseCVSS2(vectorV2);
            Object.entries(metrics).forEach(([name, obj]) => cvss_data_spanish[name] = getCVSS_v2_vectorValues(name, metrics[name].value));

        } else {
            return;
        }
    }

    return cvss_data_spanish;
}

export function getCVSS_v3_vectorValues(metric, val) {

    let values = {
        AV: getCVSSv3items(val).attackVector().value,
        AC: getCVSSv3items(val).attackComplexity().value,
        PR: getCVSSv3items(val).privilegesRequired().value,
        UI: getCVSSv3items(val).userInteraction().value,
        S: getCVSSv3items(val).scope().value,
        C: getCVSSv3items(val).confidentialityImpact().value,
        I: getCVSSv3items(val).integrityImpact().value,
        A: getCVSSv3items(val).availabilityImpact().value,
    }

    return values[metric]["label"];

}

export function getCVSS_v2_vectorValues(metric, val) {

    let values = {
        AV: getCVSSv2items(val).attackVector().value,
        AC: getCVSSv2items(val).attackComplexity().value,
        Au: getCVSSv2items(val).authentication().value,
        C: getCVSSv2items(val).confidentialityImpact().value,
        I: getCVSSv2items(val).integrityImpact().value,
        A: getCVSSv2items(val).availabilityImpact().value,
    }

    return values[metric]["label"];

}

export function getCVSSv3items(val) {

    function attackVector() {

        const labels = {
            Network: 'Red',
            Adjacent: 'Adyacente',
            Local: 'Local',
            Physical: 'Físico',
        };

        const descriptions = {
            Network: 'Cualquier red desde la que se pueda llegar al componente',
            Adjacent: 'Limitado a redes adyacentes o locales',
            Local: 'Limitado a accesos locales, por ejemplo, a través de conexiones lógicas ya establecidas (SSH, RDP, etc)',
            Physical: 'Requiere que el atacante posea acceso físico al componente',
        };

        return {
            items: getCVSSobject(labels, descriptions),
            value: {
                label: labels[val],
                description: descriptions[val]
            }
        }
    }

    function attackComplexity() {
        const labels = {
            Low: 'Baja',
            High: 'Alta',
        };

        const descriptions = {
            Low: 'No existen condiciones de acceso especializadas o atenuantes. Un atacante puede esperar un éxito repetido en contra del componente vulnerable.',
            High: 'Un ataque exitoso depende de condiciones que están más alla del control del atacante. Es decir, un ataque exitoso no puede ser logrado a voluntad, sino que requiere que el atacante realice un esfuerzo considerable en la preparación o la ejecución del ataque contra el componente vulnerable antes de esperar un ataque exitoso. Por ejemplo, un ataque exitoso podría requerir que el atacante recopile información acerca del entorno en el que existe el componente/objetivo vulnerable; que prepare dicho entorno para aumentar la confiabilidad de éxito de un exploit, o insertarse detro del path de la red lógica entre el objetivo y el recurso solicitado por la víctica y así leet y/o modificar la comunicación de la red (ej: man in the middle).',
        };


        return {
            items: getCVSSobject(labels, descriptions),
            value: {
                label: labels[val],
                description: descriptions[val]
            }
        }
    }

    function privilegesRequired() {
        const labels = {
            None: 'Ninguno',
            Low: 'Bajo',
            High: 'Alto',
        };

        const descriptions = {
            None: 'El atacante no está autorizado antes de realizar el ataque, y por lo tanto no requiere ningún tipo de acceso a configuraciones o archivos para realizar un ataque.',
            Low: 'El atacante está autorizado con privilegios que proveen de capacidades a un usuario básico que normalmente podrían afectar sólo configuraciones y archivos que pertenecen a un usuario. Alternativamente, un atacante con privilegios bajos podrìa tener la habilidad para causar impacto sólo a recursos no sensibles.',
            High: 'El atacante está autorizado con privilegios que proveen un control significativo (ej: administrador) sobre el componente vulnerable que podría afectar configuraciones y archivos de todo el componente.',
        };

        return {
            items: getCVSSobject(labels, descriptions),
            value: {
                label: labels[val],
                description: descriptions[val]
            }
        }
    }

    function userInteraction() {
        const labels = {
            None: 'Ninguna',
            Required: 'Requerida',
        };

        const descriptions = {
            None: 'El sistema vulnerable puede ser explotado sin interacción de algún usuario.',
            Required: 'La vulnerabilidad requiere que un usuario ejecute alguna acción antes de poder ser explorada con éxito. Por ejemplo, un ataque sólo podría ser posible durante la instalación de una aplicación ejecutada por un administrador del sistema.',
        };

        return {
            items: getCVSSobject(labels, descriptions),
            value: {
                label: labels[val],
                description: descriptions[val]
            }
        }
    }

    function scope() {

        const labels = {
            Unchanged: 'Sin cambios',
            Changed: 'Cambiado'
        };

        const descriptions = {
            Unchanged: 'Una vulnerabilidad explotada puede afectar solamente recursos administrados por la misma autoridad de seguridad. En este caso, el componente vulnerable y el componente impactado son el mismo, o ambos están administrados por la misma autoridad de seguridad.',
            Changed: 'Una vulnerabilidad explotada puede afectar recursos que superan el alcance de la autoridad de seguridad que administra el componente vulnerable. En este caso, el componente vulnerable y el componente impactado son distintos y administrados por distintas autoridades de seguridad.',
        };

        return {
            items: getCVSSobject(labels, descriptions),
            value: {
                label: labels[val],
                description: descriptions[val]
            }
        }
    }

    function confidentialityImpact() {

        const labels = {
            None: 'Ninguna',
            Low: 'Baja',
            High: 'Alta',

        };

        const descriptions = {
            None: 'No hay pérdida de confidencialidad en el componente vulnerable',
            Low: 'El atacante podría llegar a obtener información limitada o no tener control sobre a qué información puede acceder',
            High: 'El atacante podría obtener acceso completo a la información u obtener información que representa un impacto crítico',

        };

        return {
            items: getCVSSobject(labels, descriptions),
            value: {
                label: labels[val],
                description: descriptions[val]
            }
        }
    }


    function integrityImpact() {

        const labels = {
            None: 'Ninguna',
            Low: 'Baja',
            High: 'Alta',

        };

        const descriptions = {
            None: 'No hay pérdida de integridad en el componente vulnerable',
            Low: 'El atacante podría modificar datos, pero no tiene el control sobre cuáles o las capacidades de modificación son limitadas.',
            High: 'El atacante podría acceder a toda la información asociada al compoenente y/o podría realizar modificaciones que tengan consecuencias serias en el componente',

        };

        return {
            items: getCVSSobject(labels, descriptions),
            value: {
                label: labels[val],
                description: descriptions[val]
            }
        }
    }

    function availabilityImpact() {

        const labels = {
            None: 'Ninguna',
            Low: 'Baja',
            High: 'Alta',

        };
        const descriptions = {
            None: 'No hay pérdida de disponibilidad en el componente vulnerable',
            Low: 'El atacante podría reducir el rendimiento o provocar interrupciones en el servicio, pero sin tener la capacidad completa de denegar el acceso a usuarios legítimos',
            High: 'El atacante tiene la capacidad completa de denegar el servicio a los usuarios legítimos',
        };

        return {
            items: getCVSSobject(labels, descriptions),
            value: {
                label: labels[val],
                description: descriptions[val]
            }
        }
    }

    return {
        attackVector: attackVector,
        attackComplexity: attackComplexity,
        privilegesRequired: privilegesRequired,
        userInteraction: userInteraction,
        scope: scope,
        confidentialityImpact: confidentialityImpact,
        integrityImpact: integrityImpact,
        availabilityImpact: availabilityImpact
    }
}

export function getCVSSv2items(val) {

    function attackVector() {

        const labels = {
            N: 'Red',
            A: 'Adyacente',
            L: 'Local',
        };

        const descriptions = {
            N: 'Cualquier red desde la que se pueda llegar al componente',
            A: 'Limitado a redes adyacentes o locales',
            L: 'Limitado a accesos locales, por ejemplo, a través de conexiones lógicas ya establecidas (SSH, RDP, etc)',
        };

        return {
            items: getCVSSobject(labels, descriptions),
            value: {
                label: labels[val],
                description: descriptions[val]
            }
        }
    }

    function attackComplexity() {
        const labels = {
            L: 'Baja',
            M: 'Media',
            H: 'Alta',
        };

        const descriptions = {
            L: 'No existen condiciones de acceso especializadas o atenuantes. Un atacante puede esperar un éxito repetido en contra del componente vulnerable.',
            M: '',
            H: 'Un ataque exitoso depende de condiciones que están más alla del control del atacante. Es decir, un ataque exitoso no puede ser logrado a voluntad, sino que requiere que el atacante realice un esfuerzo considerable en la preparación o la ejecución del ataque contra el componente vulnerable antes de esperar un ataque exitoso. Por ejemplo, un ataque exitoso podría requerir que el atacante recopile información acerca del entorno en el que existe el componente/objetivo vulnerable; que prepare dicho entorno para aumentar la confiabilidad de éxito de un exploit, o insertarse detro del path de la red lógica entre el objetivo y el recurso solicitado por la víctica y así leet y/o modificar la comunicación de la red (ej: man in the middle).',
        };


        return {
            items: getCVSSobject(labels, descriptions),
            value: {
                label: labels[val],
                description: descriptions[val]
            }
        }
    }

    function confidentialityImpact() {

        const labels = {
            N: 'Ninguna',
            P: 'Parcial',
            C: 'Completa'
        };

        const descriptions = {
            N: 'No hay pérdida de confidencialidad en el componente vulnerable',
            P: 'El atacante podría llegar a obtener información limitada o no tener control sobre a qué información puede acceder',
            C: 'El atacante podría obtener acceso completo a la información u obtener información que representa un impacto crítico',
        };

        return {
            items: getCVSSobject(labels, descriptions),
            value: {
                label: labels[val],
                description: descriptions[val]
            }
        }
    }


    function integrityImpact() {

        const labels = {
            N: 'Ninguna',
            P: 'Parcial',
            C: 'Completa'
        };

        const descriptions = {
            N: 'No hay pérdida de integridad en el componente vulnerable',
            P: 'El atacante podría modificar datos, pero no tiene el control sobre cuáles o las capacidades de modificación son limitadas.',
            C: 'El atacante podría acceder a toda la información asociada al compoenente y/o podría realizar modificaciones que tengan consecuencias serias en el componente',
        };

        return {
            items: getCVSSobject(labels, descriptions),
            value: {
                label: labels[val],
                description: descriptions[val]
            }
        }
    }

    function availabilityImpact() {

        const labels = {
            N: 'Ninguna',
            P: 'Parcial',
            C: 'Completa'
        };
        const descriptions = {
            N: 'No hay pérdida de disponibilidad en el componente vulnerable',
            P: 'El atacante podría reducir el rendimiento o provocar interrupciones en el servicio, pero sin tener la capacidad completa de denegar el acceso a usuarios legítimos',
            C: 'El atacante tiene la capacidad completa de denegar el servicio a los usuarios legítimos',
        };

        return {
            items: getCVSSobject(labels, descriptions),
            value: {
                label: labels[val],
                description: descriptions[val]
            }
        }
    }

    function authentication() {
        const labels = {
            N: 'Ninguna',
            S: 'Simple',
            M: 'Múltiple'
        }
        const descriptions = {
            N: 'Explotar la vulnerabilidad no requiere autenticación.',
            S: 'Explotar la vulnerabilidad requiere una sola autenticación.',
            M: 'Explotar la vulnerabilidad requiere múltiples autenticaciones, incluso si se utiliza la misma contraseña varias veces. Un ejemplo es un atacante autenticándose en un sistema operativo además de utilizar credenciales para acceder a una aplicación alojada en ese sistema.'
        };
        return {
            items: getCVSSobject(labels, descriptions),
            value: {
                label: labels[val],
                description: descriptions[val]
            }
        }
    }

    return {
        attackVector: attackVector,
        attackComplexity: attackComplexity,
        authentication: authentication,
        confidentialityImpact: confidentialityImpact,
        integrityImpact: integrityImpact,
        availabilityImpact: availabilityImpact
    }
}

export function getCVSSobject(labels, descriptions) {

    let items = [];
    Object.keys(labels).forEach(k => {
        items.push({
            label: labels[k],
            description: descriptions[k],

        });
    });

    return items;
}


export function parseCVSS2(cvss_vector) {
    let cvss_vector_attackVector = cvss_vector.split("AC:")[0].split("AV:")[1].split("/")[0];
    let cvss_vector_complexity = cvss_vector.split("Au:")[0].split("AC:")[1].split("/")[0];
    let cvss_vector_auth = cvss_vector.split("/C:")[0].split("Au:")[1].split("/")[0];
    let cvss_vector_confidentiality = cvss_vector.split("I:")[0].split("/C:")[1].split("/")[0];;
    let cvss_vector_integrity = cvss_vector.split("A:")[0].split("I:")[1].split("/")[0];;
    let cvss_vector_availability = cvss_vector.split("A:")[1];

    return {
        'AV': { value: cvss_vector_attackVector },
        'AC': { value: cvss_vector_complexity },
        'Au': { value: cvss_vector_auth },
        'C': { value: cvss_vector_confidentiality },
        'I': { value: cvss_vector_integrity },
        'A': { value: cvss_vector_availability },
    }
}

export function getDecimalPart(value) {
    return value % 1.0;
};

export const labels = {
    //good     : 'Buena',
    info: 'Info',
    //none     : 'Ninguna',
    low: 'Baja',
    medium: 'Media',
    high: 'Alta',
    critical: 'Crítica',
};


export const kinds = () =>{
    setLocale();
    return[
        { name: getStore(format)("vulnerability.kind.ad"), id: "AD-" },
        { name: getStore(format)("vulnerability.kind.asvs"), id: "ASVS-" },
        { name: getStore(format)("vulnerability.kind.cve"), id: "CVE-" },
        { name: getStore(format)("vulnerability.kind.info"), id: "INFO-" },
        { name: getStore(format)("vulnerability.kind.leak"), id: "LEAK-" },
        { name: getStore(format)("vulnerability.kind.nvt"), id: "NVT-" },
        { name: getStore(format)("vulnerability.kind.sve"), id: "SVE-" },
        { name: getStore(format)("vulnerability.kind.eol"), id: "EOL-" },
        { name: getStore(format)("vulnerability.kind.dmarc"), id: "DMARC-" },
        { name: getStore(format)("vulnerability.kind.spf"), id: "SPF-" },
        { name: getStore(format)("vulnerability.kind.m365"), id: "M365-" },
        { name: getStore(format)("vulnerability.kind.code"), id: "CODE-" },
        { name: getStore(format)("vulnerability.kind.custom"), id: "X-" }
    ]
}

export default {
    fetchVulnerabilities,
    findVulnerability,
    fetchCWE,
    findCWE,
    fetchVulnerabilitiesProperties,
    fetchVulnerabilitiesPropertiesCount,
    fetchVulnerabilitiesPropertiesCountByKind,
    kb_url: KB_URL,
    getNote,
    upsertNote,
    deleteNote,
    buildCVEtitles,
    labels,
    buildCVSSvector,
    attackVectorOrder,
    parseCVSS2,
    parseCVSSvector,
    getCVSS_v2_vectorValues,
    getCVSS_v3_vectorValues,
    getCVSSv3items,
    getDecimalPart,
    kinds
}